import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { AppContainer } from "@bfc-software/pallet";
import { useContext, useEffect } from "react";
import {
  AuthContext,
  AuthUtils,
  logger,
  AUTHENTICATED,
  HYDRATING,
  CREATING_SESSION,
} from "@bfc-software/app-toolbox";

export const Route = createFileRoute("/$lang/_layout/orgs/$org/_layout")({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const { state } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    // Transient states that already hold a valid token - sit tight rather than
    // treating them as signed out. CREATING_SESSION is reached on every
    // warehouse switch, and bouncing to login there sent the user to the
    // product picker by way of a login screen that immediately redirected them
    // back out, losing the page they were on.
    if (state.status === HYDRATING || state.status === CREATING_SESSION) return;

    // Not authenticated - redirect to login
    if (state.status !== AUTHENTICATED) {
      logger.warn("User is not authenticated, redirecting to login");
      navigate({
        to: "/$lang/auth/login",
        params: { lang: params.lang },
      });
      return;
    }

    // Check if the current route org matches an org in state
    const orgs = AuthUtils.getOrgs(state);
    const currentRouteOrg = orgs.find(
      (org) => org.org_slug === params.org || org.org_id === params.org,
    );

    // Route matches - nothing to do
    if (currentRouteOrg) return;

    // Route org doesn't match - redirect to first available. A warehouse is
    // deliberately not required to get this far: the tile screen has no
    // warehouse, and tools like analytics span them.
    const [firstOrg] = orgs;
    const orgSlug = firstOrg
      ? AuthUtils.getOrgSlug(state, firstOrg.org_id)
      : null;

    if (!firstOrg || !orgSlug) {
      logger.warn("Unable to find a valid org to redirect the user to");
      navigate({
        to: "/$lang/auth/login",
        params: { lang: params.lang },
      });
      return;
    }

    logger.info(`Navigating user to org='${orgSlug}'`);

    // The tile screen, not a tool. Which tools this organisation can open is
    // its own decision, made there.
    navigate({
      to: "/$lang/orgs/$org",
      params: { lang: params.lang, org: orgSlug },
    });
  }, [state, params.org, params.lang, navigate]);

  // Nothing to draw while a redirect is pending. A warehouse switch keeps the
  // current page mounted rather than blanking it, since the session is being
  // re-minted underneath, not established.
  if (state.status === CREATING_SESSION) {
    return (
      <AppContainer>
        <Outlet />
      </AppContainer>
    );
  }

  if (state.status === HYDRATING || state.status !== AUTHENTICATED) {
    return <></>;
  }

  const orgs = AuthUtils.getOrgs(state);

  const currentRouteOrg = orgs.find(
    (org) => org.org_slug === params.org || org.org_id === params.org,
  );

  if (!currentRouteOrg) {
    return <></>;
  }

  return (
    <AppContainer>
      <Outlet />
    </AppContainer>
  );
}
